/**
 * Aspect Ratio Conversion Hook
 * 
 * React hook for managing aspect ratio conversion state
 * Provides conversion mode, adjustments, and helper functions
 */

import { useState, useCallback } from 'react';
import {
  MediaItem,
  MediaAdjustments,
  MediaPosition,
  MediaScale,
  ConversionMode
} from '../types';
import { AspectRatioConversionService } from '../services/aspectRatioConversion/aspectRatioConversionService';

interface UseAspectRatioConversionReturn {
  // State
  isConversionMode: boolean;
  conversionMode: ConversionMode;
  sourceAspectRatio: string | null;
  targetAspectRatio: string | null;

  // Actions
  startConversion: (fromRatio: string, toRatio: string) => void;
  cancelConversion: () => void;
  updateMediaItemPosition: (items: MediaItem[], itemId: string, position: Partial<MediaPosition>) => MediaItem[];
  updateMediaItemScale: (items: MediaItem[], itemId: string, scale: Partial<MediaScale>) => MediaItem[];
  resetMediaItem: (items: MediaItem[], itemId: string) => MediaItem[];
  resetAllMediaItems: (items: MediaItem[]) => MediaItem[];
  initializeMediaItems: (items: MediaItem[]) => MediaItem[];
  applyAdjustmentsToMediaItems: (items: MediaItem[]) => MediaItem[];
  
  // Helper functions
  nudgePositionUp: (items: MediaItem[], itemId: string, amount?: number) => MediaItem[];
  nudgePositionDown: (items: MediaItem[], itemId: string, amount?: number) => MediaItem[];
  nudgePositionLeft: (items: MediaItem[], itemId: string, amount?: number) => MediaItem[];
  nudgePositionRight: (items: MediaItem[], itemId: string, amount?: number) => MediaItem[];
  zoomIn: (items: MediaItem[], itemId: string, amount?: number) => MediaItem[];
  zoomOut: (items: MediaItem[], itemId: string, amount?: number) => MediaItem[];
}

export const useAspectRatioConversion = (): UseAspectRatioConversionReturn => {
  const [isConversionMode, setIsConversionMode] = useState(false);
  const [conversionMode, setConversionMode] = useState<ConversionMode>('none');
  const [sourceAspectRatio, setSourceAspectRatio] = useState<string | null>(null);
  const [targetAspectRatio, setTargetAspectRatio] = useState<string | null>(null);

  /**
   * Start conversion mode
   */
  const startConversion = useCallback((fromRatio: string, toRatio: string) => {
    // Validate conversion is supported
    if (!AspectRatioConversionService.isConversionSupported(fromRatio, toRatio)) {
      console.error(`Conversion from ${fromRatio} to ${toRatio} is not supported`);
      return;
    }

    setIsConversionMode(true);
    setSourceAspectRatio(fromRatio);
    setTargetAspectRatio(toRatio);
    
    // Set conversion mode based on ratios
    if (fromRatio === '16:9' && toRatio === '9:16') {
      setConversionMode('16:9-to-9:16');
    } else if (fromRatio === '9:16' && toRatio === '16:9') {
      setConversionMode('9:16-to-16:9');
    } else {
      setConversionMode('none');
    }

    console.log(`🔄 Started conversion: ${fromRatio} → ${toRatio}`);
  }, []);

  /**
   * Cancel conversion mode
   */
  const cancelConversion = useCallback(() => {
    setIsConversionMode(false);
    setConversionMode('none');
    setSourceAspectRatio(null);
    setTargetAspectRatio(null);
    console.log('❌ Cancelled conversion mode');
  }, []);

  /**
   * Update position for a media item
   */
  const updateMediaItemPosition = useCallback(
    (items: MediaItem[], itemId: string, position: Partial<MediaPosition>): MediaItem[] => {
      return AspectRatioConversionService.updateMediaItemPosition(items, itemId, position);
    },
    []
  );

  /**
   * Update scale for a media item
   */
  const updateMediaItemScale = useCallback(
    (items: MediaItem[], itemId: string, scale: Partial<MediaScale>): MediaItem[] => {
      return AspectRatioConversionService.updateMediaItemScale(items, itemId, scale);
    },
    []
  );

  /**
   * Reset a single media item's adjustments
   */
  const resetMediaItem = useCallback((items: MediaItem[], itemId: string): MediaItem[] => {
    return items.map(item =>
      item.id === itemId ? AspectRatioConversionService.resetMediaItemAdjustments(item) : item
    );
  }, []);

  /**
   * Reset all media items' adjustments
   */
  const resetAllMediaItems = useCallback((items: MediaItem[]): MediaItem[] => {
    return AspectRatioConversionService.resetAllAdjustments(items);
  }, []);

  /**
   * Initialize media items with default adjustments
   */
  const initializeMediaItems = useCallback((items: MediaItem[]): MediaItem[] => {
    return AspectRatioConversionService.initializeMediaItemsWithDefaults(items);
  }, []);

  /**
   * Apply adjustments - for now just returns items as-is
   * Backend will handle the actual cropping
   */
  const applyAdjustmentsToMediaItems = useCallback((items: MediaItem[]): MediaItem[] => {
    return items; // Items already have position/scale properties
  }, []);

  /**
   * Nudge position up
   */
  const nudgePositionUp = useCallback(
    (items: MediaItem[], itemId: string, amount: number = 5): MediaItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          const currentPosition = item.position || { x: 0, y: 0 };
          const newPosition = AspectRatioConversionService.nudgePosition(
            currentPosition,
            0,
            amount
          );
          return { ...item, position: newPosition };
        }
        return item;
      });
    },
    []
  );

  /**
   * Nudge position down
   */
  const nudgePositionDown = useCallback(
    (items: MediaItem[], itemId: string, amount: number = 5): MediaItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          const currentPosition = item.position || { x: 0, y: 0 };
          const newPosition = AspectRatioConversionService.nudgePosition(
            currentPosition,
            0,
            -amount
          );
          return { ...item, position: newPosition };
        }
        return item;
      });
    },
    []
  );

  /**
   * Nudge position left
   */
  const nudgePositionLeft = useCallback(
    (items: MediaItem[], itemId: string, amount: number = 5): MediaItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          const currentPosition = item.position || { x: 0, y: 0 };
          const newPosition = AspectRatioConversionService.nudgePosition(
            currentPosition,
            -amount,
            0
          );
          return { ...item, position: newPosition };
        }
        return item;
      });
    },
    []
  );

  /**
   * Nudge position right
   */
  const nudgePositionRight = useCallback(
    (items: MediaItem[], itemId: string, amount: number = 5): MediaItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          const currentPosition = item.position || { x: 0, y: 0 };
          const newPosition = AspectRatioConversionService.nudgePosition(
            currentPosition,
            amount,
            0
          );
          return { ...item, position: newPosition };
        }
        return item;
      });
    },
    []
  );

  /**
   * Zoom in (increase scale)
   */
  const zoomIn = useCallback(
    (items: MediaItem[], itemId: string, amount: number = 0.1): MediaItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          const currentScale = item.scale || { scaleX: 1, scaleY: 1 };
          const newScale = AspectRatioConversionService.clampScaleObject({
            scaleX: currentScale.scaleX + amount,
            scaleY: currentScale.scaleY + amount
          });
          return { ...item, scale: newScale };
        }
        return item;
      });
    },
    []
  );

  /**
   * Zoom out (decrease scale)
   */
  const zoomOut = useCallback(
    (items: MediaItem[], itemId: string, amount: number = 0.1): MediaItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          const currentScale = item.scale || { scaleX: 1, scaleY: 1 };
          const newScale = AspectRatioConversionService.clampScaleObject({
            scaleX: currentScale.scaleX - amount,
            scaleY: currentScale.scaleY - amount
          });
          return { ...item, scale: newScale };
        }
        return item;
      });
    },
    []
  );

  return {
    // State
    isConversionMode,
    conversionMode,
    sourceAspectRatio,
    targetAspectRatio,

    // Actions
    startConversion,
    cancelConversion,
    updateMediaItemPosition,
    updateMediaItemScale,
    resetMediaItem,
    resetAllMediaItems,
    initializeMediaItems,
    applyAdjustmentsToMediaItems,

    // Helper functions
    nudgePositionUp,
    nudgePositionDown,
    nudgePositionLeft,
    nudgePositionRight,
    zoomIn,
    zoomOut
  };
};
